/**
 * HELIUM Framework - Master Product Page Web Component
 *
 * Owns the parts of the PDP that need to react to a variant change but live
 * outside the <variant-picker> itself: the hidden `<select data-variant-select>`
 * that /cart/add reads, the price display, the gallery, and the URL. The
 * variant-picker block/component isn't nested inside the buy-buttons form
 * (they're sibling blocks), so it can't reach the form on its own - it just
 * works out which variant is selected and dispatches a bubbling
 * `variant:change` event. This component listens for that event and applies
 * the result to the rest of the page.
 *
 * Scoped in a function that bails out if <product-page> is already defined:
 * this file is loaded both by the product section and globally by the
 * quick-add modal, so a product page runs it twice - and a second top-level
 * `class ProductPage` would throw "already declared".
 */
(() => {
if (customElements.get('product-page')) return;

class ProductPage extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.priceBlock = this.querySelector('[data-product-price-block]');
    this.priceElement = this.querySelector('[data-product-price]');
    this.compareAtElement = this.querySelector('[data-product-compare-at-price]');
    this.unitPriceElement = this.querySelector('[data-product-unit-price]');
    this.gallery = this.querySelector('.product-gallery');
    this.variantPicker = this.querySelector('variant-picker');
    this.hiddenSelect = this.form?.querySelector('[data-variant-select]');
    this.addToCartButton = this.querySelector('[data-add-to-cart-submit]');
    this.quantityInput = this.querySelector('[data-quantity-input]');
    this.purchaseTypes = this.querySelector('purchase-types');
    this.purchaseTypePrompt = this.querySelector('[data-purchase-type-prompt]');
    this.sellingPlanInput = this.form?.querySelector('[data-selling-plan-input]');
    // Only present when the product has real Shopify selling plans - see
    // subscription-frequency.liquid. A demo-mode placeholder select has no
    // real plan ids behind it, so it's never wired up here.
    this.sellingPlanSelects = [...this.querySelectorAll('[data-selling-plan-select]')];
    this.subscriptionMinQuantity = Number(this.dataset.subscriptionMinQuantity) || 1;
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.sellingPlanSelect = this.querySelector('[data-selling-plan-select]');
    // shop.money_format can contain HTML - currency apps often set it to
    // "<span class=money>£{{amount}}</span>" - and prices here are written as
    // plain text (textContent, or an <input>'s value), so the tags would show
    // literally. Parsing it as HTML and keeping only the text drops the tags
    // and decodes entities such as &pound;, leaving the {{amount}} placeholder.
    this.moneyFormat =
      new DOMParser().parseFromString(this.dataset.moneyFormat || '${{amount}}', 'text/html').body.textContent ||
      '${{amount}}';
    this.freeLabel = this.dataset.freeLabel || 'Free';

    const pricesScript = this.querySelector('[data-product-prices]');
    this.prices = pricesScript ? JSON.parse(pricesScript.textContent) : {};
    this.currentVariantId = this.hiddenSelect ? this.hiddenSelect.value : Object.keys(this.prices)[0];
    // null (rather than defaulting to 'one_time') when a <purchase-types>
    // selector is present but nothing is active yet - e.g. its default is
    // "none", forcing the customer to choose before they can add to cart.
    // Without a selector on the page at all, purchase type doesn't gate
    // anything, so it's treated as always "selected".
    this.purchaseType = this.purchaseTypes?.querySelector('.is-active')?.dataset.purchaseTypeOption || null;
    // The add-to-cart button's disabled attribute is rendered server-side
    // from the variant's own availability - read it once up front so
    // purchase-type gating (below) can be combined with it without
    // clobbering a genuinely sold-out variant.
    this.variantAvailable = this.addToCartButton ? !this.addToCartButton.disabled : true;

    if (this.variantPicker) {
      this.variantPicker.addEventListener('variant:change', (event) => {
        this.handleVariantChange(event.detail.variant);
      });
    }

    if (this.quantityInput) {
      this.quantityInput.addEventListener('change', () => {
        this.refreshAddToCartAvailability();
        this.updatePrice();
      });
      // Typed quantities only fire 'change' on blur - re-check the
      // subscription minimum as they type so the button unlocks straight away.
      this.quantityInput.addEventListener('input', () => this.refreshAddToCartAvailability());
    }

    if (this.purchaseTypes) {
      this.purchaseTypes.addEventListener('purchase-type:change', (event) => {
        this.purchaseType = event.detail.value;
        this.clearPurchaseTypePrompt();
        this.refreshAddToCartAvailability();
        this.updateSellingPlan();
        this.updatePrice();
      });
    }

    this.sellingPlanSelects.forEach((select) => {
      select.addEventListener('change', () => this.updateSellingPlan());
    });

    // The add-to-cart button stays natively clickable even when a purchase
    // type is required but not chosen yet (see refreshAddToCartAvailability)
    // so this submit listener - rather than a disabled attribute - is what
    // actually blocks the add and prompts the customer. Listening on the
    // form (not just the button's click) also catches an Enter-key submit.
    if (this.form) {
      this.form.addEventListener('submit', (event) => {
        if (this.purchaseTypes && !this.purchaseType) {
          event.preventDefault();
          event.stopPropagation();
          this.promptPurchaseTypeSelection();
          return;
        }

        // The button is disabled in this state, but an implicit (Enter-key)
        // submit or a third-party script could still get here.
        if (this.getSubscriptionQuantityShortfall() > 0) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        // Belt-and-braces resync right before the browser reads the form's
        // fields for submission (cart-drawer.js's document-level listener
        // builds its FormData from this same form once this handler
        // returns) - guards against the selling-plan input ever going stale
        // relative to the select's true value, e.g. a bfcache-restored page
        // where the frequency <select> can come back with a different value
        // than what was last copied into the hidden input, with no 'change'
        // event to catch it.
        this.updateSellingPlan();
      });
    }

    this.refreshAddToCartAvailability();
    this.updateSellingPlan();
  }

  /**
   * Applies a newly-selected variant (reported by <variant-picker>) to the
   * rest of the page.
   * @param {Object} variant
   */
  handleVariantChange(variant) {
    if (!variant) return;

    this.currentVariantId = variant.id;

    if (this.hiddenSelect) {
      this.hiddenSelect.value = variant.id;
      this.hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.updateMedia(variant);
    this.updateURL(variant);
    this.updateAvailability(variant);
    this.updatePrice();
    this.updateStockCounter(variant);
  }

  /**
   * Refreshes the optional stock counter block (blocks/_stock-counter.liquid)
   * for the newly selected variant. Its per-variant stock data and translated
   * labels are all rendered into the block, so this needs no request. Must
   * match the Liquid's state rules.
   * @param {Object} variant
   */
  updateStockCounter(variant) {
    const counter = this.querySelector('[data-stock-counter]');
    if (!counter) return;
    const text = counter.querySelector('[data-stock-counter-text]');
    const dataScript = counter.querySelector('[data-stock-counter-variants]');
    if (!text || !dataScript) return;

    if (!this.stockData) {
      try {
        this.stockData = JSON.parse(dataScript.textContent);
      } catch (error) {
        return;
      }
    }
    const stock = this.stockData[variant.id];
    if (!stock) return;

    const { labelInStock, labelInStockCount, labelLowStock, labelLowStockCount, labelBackorder, labelOutOfStock } =
      counter.dataset;
    const showCount = counter.dataset.showCount === 'true';
    const threshold = Number(counter.dataset.lowStockThreshold) || 0;

    let state = 'in_stock';
    if (!stock.available) state = 'out_of_stock';
    else if (!stock.tracked) state = 'untracked';
    else if (stock.quantity <= 0) state = 'backorder';
    else if (stock.quantity <= threshold) state = 'low_stock';

    const withCount = (label) => label.replace('[count]', stock.quantity);
    const labels = {
      out_of_stock: labelOutOfStock,
      backorder: labelBackorder,
      low_stock: showCount ? withCount(labelLowStockCount) : labelLowStock,
      in_stock: showCount ? withCount(labelInStockCount) : labelInStock,
      untracked: labelInStock,
    };

    counter.className = counter.className.replace(/\bstock-counter--\w+/g, '').trim();
    counter.classList.add(`stock-counter--${state}`);
    counter.hidden = state === 'out_of_stock' && counter.dataset.hideOutOfStock === 'true';
    text.textContent = labels[state] || '';
  }

  /**
   * Gets the current quantity from the quantity selector, if present.
   * @returns {number}
   */
  getQuantity() {
    const value = this.quantityInput ? Number(this.quantityInput.value) : 1;
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  /**
   * The discount percentage (0-100) configured for the currently selected
   * subscription or prepaid purchase type, otherwise 0.
   * @returns {number}
   */
  getSubscriptionDiscount() {
    if (this.purchaseType === 'subscription') return Number(this.purchaseTypes?.dataset.subscriptionDiscount) || 0;
    if (this.purchaseType === 'prepaid') return Number(this.purchaseTypes?.dataset.prepaidDiscount) || 0;
    return 0;
  }

  /**
   * The real selling-plan select for the currently selected purchase type
   * (subscription or prepaid), or null for a one-time purchase / a type
   * with no real plans behind it.
   * @returns {HTMLSelectElement|null}
   */
  getActiveSellingPlanSelect() {
    if (!this.purchaseType) return null;
    return this.sellingPlanSelects.find((select) => select.dataset.sellingPlanSelect === this.purchaseType) || null;
  }

  /**
   * Updates the displayed price (and compare-at price) for the current
   * variant, quantity, and purchase type, then keeps the add-to-cart
   * button's price-in-label in sync with the same numbers.
   */
  updatePrice() {
    const priceData = this.prices[this.currentVariantId];
    if (!priceData) return;

    const quantity = this.getQuantity();
    const unitCents = this.getUnitCents();
    const compareUnitCents = this.getSubscriptionDiscount() > 0 ? priceData.cents : priceData.compareAtCents || null;

    if (this.priceElement) {
      const totalCents = unitCents * quantity;
      this.priceElement.textContent = totalCents === 0 ? this.freeLabel : this.formatMoney(totalCents);
      this.priceElement.classList.toggle('price-free', totalCents === 0);
    }

    if (this.compareAtElement) {
      this.compareAtElement.hidden = !compareUnitCents;
      if (compareUnitCents) {
        this.compareAtElement.textContent = this.formatMoney(compareUnitCents * quantity);
      }
    }

    if (this.priceBlock) {
      this.priceBlock.classList.toggle('product-price--sale', Boolean(compareUnitCents));
    }

    this.updateAddToCartLabel();
    // Price per unit (e.g. "£1.20/100g") - pre-formatted by Liquid, per variant.
    if (this.unitPriceElement) {
      this.unitPriceElement.hidden = !priceData.unitPrice;
      this.unitPriceElement.textContent = priceData.unitPrice || '';
    }
  }

  /**
   * The current variant's unit price in cents after any subscription/prepaid
   * discount, or null when there's no price data for it.
   * @returns {number|null}
   */
  getUnitCents() {
    const priceData = this.prices[this.currentVariantId];
    if (!priceData) return null;

    const discount = this.getSubscriptionDiscount();
    return discount > 0 ? Math.round((priceData.cents * (100 - discount)) / 100) : priceData.cents;
  }

  /**
   * Sets the add-to-cart button's label, in priority order: sold out, then
   * the "Add N more to subscribe" message while a subscription/prepaid
   * purchase is short of the minimum quantity, then the normal label (with
   * the current total when the block's price-in-button setting is on).
   */
  updateAddToCartLabel() {
    if (!this.addToCartButton) return;

    const { labelBase, labelSoldOut, priceInButton, messageOne, messageOther } = this.addToCartButton.dataset;

    if (!this.variantAvailable) {
      this.addToCartButton.value = labelSoldOut;
      return;
    }

    const shortfall = this.getSubscriptionQuantityShortfall();
    if (shortfall > 0) {
      this.addToCartButton.value = shortfall === 1 ? messageOne : messageOther.replace('[count]', String(shortfall));
      return;
    }

    const unitCents = this.getUnitCents();
    if (priceInButton !== 'true' || unitCents === null) {
      this.addToCartButton.value = labelBase;
      return;
    }

    const totalCents = unitCents * this.getQuantity();
    const priceText = totalCents === 0 ? this.freeLabel : this.formatMoney(totalCents);
    this.addToCartButton.value = `${labelBase} | ${priceText}`;
  }

  /**
   * Formats an amount in cents using the shop's money format
   * (`{{ shop.money_format }}`, e.g. "£{{amount}}").
   * @param {number} cents
   * @returns {string}
   */
  formatMoney(cents) {
    const placeholder = this.moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    const kind = placeholder ? placeholder[1] : 'amount';

    let precision = 2;
    let thousands = ',';
    let decimal = '.';

    if (kind === 'amount_no_decimals') precision = 0;
    if (kind === 'amount_with_comma_separator') {
      thousands = '.';
      decimal = ',';
    }
    if (kind === 'amount_no_decimals_with_comma_separator') {
      precision = 0;
      thousands = '.';
      decimal = ',';
    }

    const [whole, fraction] = (cents / 100).toFixed(precision).split('.');
    const groupedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const value = fraction ? `${groupedWhole}${decimal}${fraction}` : groupedWhole;

    return placeholder ? this.moneyFormat.replace(placeholder[0], value) : value;
  }

  /**
   * Moves the gallery to the selected variant's featured image - scrolls the
   * tiles grid into view when present, and (since a tiles gallery still
   * falls back to a swiper on mobile, and a 'swiper' gallery style uses one
   * on every breakpoint) also slides the swiper to the matching slide when
   * one is initialized. Swiper attaches itself as `.swiper` on its container
   * element, so no import is needed here to read the live instance.
   * @param {Object} variant
   */
  updateMedia(variant) {
    if (!this.gallery) return;
    if (this.gallery.dataset.mediaSource !== 'variant') return;
    if (this.gallery.dataset.singleOption !== 'true') return;
    // Gallery items carry the product *media* id (the gallery loops
    // product.media), which is what variant.featured_media.id refers to.
    if (!variant.featured_media) return;

    const mediaId = String(variant.featured_media.id);

    this.gallery.querySelectorAll('[data-media-id]').forEach((el) => el.classList.remove('is-active'));

    const tileTarget = this.gallery.querySelector(`.product-gallery__tiles [data-media-id="${mediaId}"]`);
    if (tileTarget) {
      tileTarget.classList.add('is-active');
      tileTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }

    const swiperEl = this.gallery.querySelector('.product-gallery__swiper');
    if (swiperEl) {
      const slides = Array.from(swiperEl.querySelectorAll('[data-media-id]'));
      const slideIndex = slides.findIndex((el) => el.dataset.mediaId === mediaId);
      if (slideIndex > -1) {
        slides[slideIndex].classList.add('is-active');
        if (swiperEl.swiper) swiperEl.swiper.slideTo(slideIndex);
      }
    }
  }

  /**
   * Keeps browser URL updated with ?variant=12345678 without refreshing the page
   * @param {Object} variant
   */
  updateURL(variant) {
    // A <product-page> rendered inside the quick-add modal is a preview of
    // a possibly different product than the one the customer is actually
    // on - it must never rewrite the real page's address bar.
    if (this.closest('quick-add-modal')) return;
    window.history.replaceState({}, '', `?variant=${variant.id}`);
  }

  /**
   * Records the selected variant's availability and refreshes the
   * add-to-cart button (state and label) accordingly.
   * @param {Object} variant
   */
  updateAvailability(variant) {
    this.variantAvailable = Boolean(variant.available);
    this.refreshAddToCartAvailability();
  }

  /**
   * Disables add-to-cart when the selected variant is unavailable. When a
   * <purchase-types> selector is present but the customer hasn't picked
   * one-time/subscription yet (its default is "none", so nothing is active
   * until they choose), the button is left natively enabled - just dimmed
   * via `is-awaiting-selection` - because a genuinely `disabled` button
   * can't be clicked at all, and the whole point is that clicking it (or
   * pressing Enter) is what triggers the prompt in promptPurchaseTypeSelection()
   * (see the form 'submit' listener in connectedCallback). Sold-out takes
   * priority over the purchase-type prompt since it's the more specific,
   * unrecoverable-by-the-customer reason.
   */
  refreshAddToCartAvailability() {
    if (!this.addToCartButton) return;

    if (!this.variantAvailable) {
      this.addToCartButton.disabled = true;
      this.addToCartButton.classList.remove('is-awaiting-selection');
    } else {
      this.addToCartButton.disabled = this.getSubscriptionQuantityShortfall() > 0;

      const purchaseTypeChosen = !this.purchaseTypes || Boolean(this.purchaseType);
      this.addToCartButton.classList.toggle('is-awaiting-selection', !purchaseTypeChosen);
    }

    this.updateAddToCartLabel();
  }

  /**
   * How many more units the customer needs to add to meet the product
   * section's "Minimum quantity for subscription" setting, or 0 when it's
   * met / doesn't apply. Applies to any purchase type other than one-time
   * (subscription and prepaid).
   * @returns {number}
   */
  getSubscriptionQuantityShortfall() {
    if (!this.purchaseType || this.purchaseType === 'one_time') return 0;
    // Read the raw input rather than getQuantity(), which floors a blank or
    // mid-edit value to 1 - still short of any minimum above 1.
    const quantity = Math.max(0, Math.floor(Number(this.quantityInput?.value ?? 1)) || 0);
    return Math.max(0, this.subscriptionMinQuantity - quantity);
  }

  /**
   * Highlights the purchase-type selector and shows an explanatory message
   * when the customer tries to add to cart without choosing one - called
   * from the form's 'submit' listener. Re-triggers the CSS shake animation
   * on repeated attempts by forcing a reflow between removing and re-adding
   * the class, since re-adding an already-present class alone wouldn't
   * restart it.
   */
  promptPurchaseTypeSelection() {
    if (!this.purchaseTypes) return;

    this.purchaseTypes.classList.remove('is-invalid');
    void this.purchaseTypes.offsetWidth;
    this.purchaseTypes.classList.add('is-invalid');

    if (this.purchaseTypePrompt) this.purchaseTypePrompt.hidden = false;

    this.purchaseTypes.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.purchaseTypes.querySelector('input[data-purchase-type-option], button[data-purchase-type-option]')?.focus();
  }

  /**
   * Clears the highlight/message from promptPurchaseTypeSelection() once
   * the customer actually picks a purchase type.
   */
  clearPurchaseTypePrompt() {
    this.purchaseTypes?.classList.remove('is-invalid');
    if (this.purchaseTypePrompt) this.purchaseTypePrompt.hidden = true;
  }

  /**
   * Keeps the add-to-cart form's hidden selling-plan field in sync with the
   * real selling-plan select (only rendered when the product has actual
   * Shopify selling plans - see subscription-frequency.liquid). Giving the
   * hidden input a `name` is what makes /cart/add treat this as a
   * subscription order; it's left unnamed - and so left out of the
   * submitted form entirely - for a one-time purchase, or for a demo-only
   * frequency select with no real plan id behind it.
   */
  updateSellingPlan() {
    if (!this.sellingPlanInput) return;

    const select = this.getActiveSellingPlanSelect();
    if (select) {
      this.sellingPlanInput.name = 'selling_plan';
      this.sellingPlanInput.value = select.value;
    } else {
      this.sellingPlanInput.removeAttribute('name');
      this.sellingPlanInput.value = '';
    }
  }

  /**
   * The selling plan id that should be submitted for the current
   * purchase-type/frequency choice, or '' for a one-time purchase - computed
   * straight from the live select rather than read back off
   * `sellingPlanInput`. Third-party subscription app embeds (e.g. Seal
   * Subscriptions) commonly watch the cart form for a `selling_plan` field
   * and take over managing it, which can stomp the value our own
   * updateSellingPlan() just wrote before the request actually goes out.
   * cart-drawer.js calls this to force the correct id into the outgoing
   * request body directly, rather than trusting whatever's currently sitting
   * in the DOM input at submit time.
   * @returns {string}
   */
  getSellingPlanId() {
    return this.getActiveSellingPlanSelect()?.value || '';
  }
}

customElements.define('product-page', ProductPage);
})();
